import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";

/**
 * Detect whether this session should render in "TV mode":
 *   - 10-foot layout (larger type, taller targets)
 *   - side navigation instead of bottom tabs
 *   - AdMob interstitials suppressed (banner-only)
 *   - focus-visible rings are always rendered (D-pad users always want them)
 *
 * Signals we consider, in priority order:
 *   1. `?tv=1` / `?tv=0` URL query — dev-only override.
 *   2. `localStorage["orvis:tv"]` — persistent override.
 *   3. Native Android: the platform's TV signal via the OrvisTvChannels
 *      plugin (resolved before first render — see resolveNativeTvMode).
 *   4. User-Agent contains "Android TV" or "SmartTV", or Chromium's
 *      TV WebView reports as "TV" — the standard Android TV signal.
 *   5. `matchMedia('(pointer: none) and (hover: none)')` — TVs with
 *      remotes only, no touch/mouse.
 *   6. Capacitor's platform info is Android AND the WebView has no
 *      touchscreen — some Android TVs get filtered here by Google Play
 *      leanback checks but we belt-and-suspenders locally too.
 *
 * The result is cached at hook init and does NOT change with viewport
 * — TVs don't resize, and we don't want a hover-detect false-positive
 * on a desktop with a bluetooth mouse mid-session.
 */

const STORAGE_KEY = "orvis:tv";

/**
 * Set once by `resolveNativeTvMode()` (awaited in main.tsx before the first
 * render). Android TV WebViews don't put "Android TV" in their user agent
 * and an emulator even reports a fine pointer, so on native Android the
 * platform's own answer (UiModeManager / leanback feature) is what counts.
 */
let nativeTv: boolean | undefined;

export async function resolveNativeTvMode(): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return;
  if (!Capacitor.isPluginAvailable("OrvisTvChannels")) return;
  try {
    const { OrvisTvChannels } = await import("../native.js");
    nativeTv = (await OrvisTvChannels.isTelevision()).television;
  } catch {
    /* older native shell without the method — fall back to heuristics */
  }
}

function detectTv(): boolean {
  try {
    const params = new URLSearchParams(location.search);
    const q = params.get("tv");
    if (q === "1" || q === "true") return true;
    if (q === "0" || q === "false") return false;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch { /* silent */ }

  if (nativeTv !== undefined) return nativeTv;

  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent || "";
    // Chromium on Android TV explicitly identifies itself. Some Fire TV
    // and Roku browsers also include these tokens.
    if (/Android TV|SmartTV|SMART-TV|GoogleTV|Leanback/i.test(ua)) return true;
  }

  if (typeof matchMedia !== "undefined") {
    // The remote-only, no-touch signal. Excludes mobile (coarse pointer,
    // no hover) and desktop with a mouse (fine pointer, hover).
    const remoteOnly = matchMedia("(pointer: none) and (hover: none)");
    if (remoteOnly.matches) return true;
  }

  // Belt-and-suspenders for older Android TVs whose UA isn't tagged.
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
    // No good runtime signal from just @capacitor/core; leave to UA + media.
  }

  return false;
}

export function useTvMode(): boolean {
  const [tv] = useState<boolean>(() => detectTv());

  // Stamp a class on <html> so pure-CSS rules can conditionally apply
  // 10-foot scaling. The Tailwind config picks it up via `tv:` variant.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("tv", tv);
  }, [tv]);

  return tv;
}
